import { DeleteFilled } from '@ant-design/icons';
import chatApi from 'api/chatApi';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import InfomationIcon from 'assets/icons/InfomationIcon';
import { showConfirmModal, showWarningModal } from 'components/Modals/CommonModals';
import { onChangeAuthPage } from 'features/home/homeSlice';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import { ChatMessageType, ChatMessageTypeRequest } from 'models/chatMessage';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { selectCurrentLanguage, selectCurrentUser } from 'redux/userSlice';
import { getUserColor } from 'utils/colorUtils';
import { checkEmptyString } from 'utils/commonUtils';
import { formatCompactNumber } from 'utils/formatAmount';
import ChatInput from './ChatInput';
import RenderMeassage from './RenderMessage';
import { useChatConntext } from 'components/Hoc/MessageProvider';
import { useSelector } from 'react-redux';

interface OtherMessageProps {
  chat: ChatMessageType;
  isAssistantChat?: boolean;
  onScrollMessage: (messageId: string) => void;
  canDelete?: boolean;
}

const OtherMessage: React.FunctionComponent<OtherMessageProps> = ({
  chat,
  isAssistantChat = true,
  onScrollMessage,
  canDelete = false,
}) => {
  const { currentTheme } = useThemes();
  const t = useAppTranslation();
  const [showReplies, setShowReplies] = useState<boolean>(false);
  const [activeReplyInputId, setActiveReplyInputId] = useState<string | null>(null);
  const deleteMessage = async (messageId: string) => {
    await chatApi.deleteMessage(messageId);
  };

  const handleDeleteMessage = (messageId: string) => {
    showConfirmModal({
      content: t('confirmModal.chat-message.confirm-content'),
      width: 360,
      icon: <InfomationIcon />,
      onOk: () => {
        deleteMessage(messageId);
      },
      okText: t('button.confirm'),
      cancelText: t('button.cancel'),
    });
  };

  const handleShowReplies = () => {
    setShowReplies((prev) => !prev);
  };

  const handleSetActiveReplyInput = (messageId: string | null) => {
    setActiveReplyInputId(messageId);
  };

  return (
    <div className="chatbox-container">
      <div className={`chat-message chat-message-mobile`}>
        <MessageItem
          chat={chat}
          onScrollMessage={onScrollMessage}
          canDelete={canDelete}
          handleDeleteMessage={handleDeleteMessage}
          activeReplyInputId={activeReplyInputId}
          onSetActiveReplyInput={handleSetActiveReplyInput}
        />
        {chat?.replies?.length > 0 && (
          <div className="show-replies-button cursor-pointer" onClick={handleShowReplies}>
            {showReplies ? (
              <IoChevronUpOutline
                color={currentTheme === 'dark' ? 'black' : 'white'}
                width={12}
                height={12}
              />
            ) : (
              <IoChevronDownOutline
                color={currentTheme === 'dark' ? 'black' : 'white'}
                width={12}
                height={12}
              />
            )}

            <span className="cursor-pointer">
              {chat.replies.length} {t('comment.reply')}
            </span>
          </div>
        )}
        {showReplies && (
          <div className="reply-message_container cursor-pointer">
            {chat.replies.map((reply) => (
              <MessageItem
                chat={reply}
                key={reply.messageId}
                onScrollMessage={onScrollMessage}
                canDelete={canDelete}
                handleDeleteMessage={handleDeleteMessage}
                activeReplyInputId={activeReplyInputId}
                onSetActiveReplyInput={handleSetActiveReplyInput}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OtherMessage;

interface MessageItemProps {
  chat: ChatMessageType;
  onScrollMessage: (messageId: string) => void;
  canDelete?: boolean;
  handleDeleteMessage: (messageId: string) => void;
  activeReplyInputId: string | null;
  onSetActiveReplyInput: (messageId: string | null) => void;
}

const MessageItem: React.FunctionComponent<MessageItemProps> = ({
  chat,
  onScrollMessage,
  canDelete = false,
  handleDeleteMessage,
  activeReplyInputId,
  onSetActiveReplyInput,
}) => {
  const t = useAppTranslation();
  const language = useAppSelector(selectCurrentLanguage);
  const [likeState, setLikeState] = useState({
    isLiked: false,
    likedCounter: 0,
    isDisliked: false,
    dislikedCounter: 0,
  });
  const { isUserLoggedIn, currentUser } = useUserDetail();

  const dispatch = useAppDispatch();

  useEffect(() => {
    setLikeState({
      isLiked: currentUser && isUserLoggedIn ? chat.likeUsers.includes(currentUser.id) : false,
      likedCounter: chat.likeUsers.length,
      isDisliked:
        currentUser && isUserLoggedIn ? chat.dislikeUsers.includes(currentUser.id) : false,
      dislikedCounter: chat.dislikeUsers.length,
    });
  }, [chat, currentUser?.id, isUserLoggedIn]);

  const likeChat = useCallback(
    async (isLike: boolean) => {
      if (!isUserLoggedIn) {
        dispatch(onChangeAuthPage(0));
      }
      await chatApi.likeChat({ messageId: chat.messageId, like: isLike });
    },
    [chat, likeState, isUserLoggedIn],
  );

  return (
    <div>
      <span className="message">
        <div className={`message-data-name selected-mobile`}>
          <div style={{ color: getUserColor(chat.userId) }}>
            {checkEmptyString(chat.username) ? t('anonymous') : chat.username}
          </div>
          <div className="message-data-time">{` ${moment(chat.date)
            .locale(language === 'zh' ? 'zh-cn' : language)
            .fromNow()}`}</div>
        </div>
        <RenderMeassage chat={chat} myMessage={false} />
      </span>
      {canDelete && (
        <span className="delete-icon" onClick={() => handleDeleteMessage(chat.messageId)}>
          <DeleteFilled width={12} height={12} />
        </span>
      )}
      <ReactionButtonChat chat={chat} likeChat={likeChat} likeState={likeState} />
    </div>
  );
};

interface ReactionButtonChatProps {
  chat: ChatMessageType;
  likeChat: (isLike: boolean) => void;
  likeState: {
    isLiked: boolean;
    likedCounter: number;
    isDisliked: boolean;
    dislikedCounter: number;
  };
}

const ReactionButtonChat: React.FunctionComponent<ReactionButtonChatProps> = ({
  chat,
  likeChat,
  likeState,
}) => {
  const t = useAppTranslation();
  const { globalMessages, sendMessage } = useChatConntext();
  const { isUserLoggedIn } = useUserDetail();
  const user = useSelector(selectCurrentUser);
  const { activeReplyId, handleSetActiveReply } = useChatConntext();

  const dispatch = useAppDispatch();

  const handleReplyClick = useCallback(() => {
    if (isUserLoggedIn) {
      if (user && user.blockChat) {
        showWarningModal({
          content: t('chat.warning'),
          onOk: () => {},
          title: '',
        });
      } else {
        if (activeReplyId === chat.messageId) {
          handleSetActiveReply(null);
        } else {
          handleSetActiveReply(chat.messageId);
        }
      }
    } else {
      dispatch(onChangeAuthPage(0));
    }
  }, [activeReplyId, chat.messageId, isUserLoggedIn, user?.blockChat]);

  const onSendMessage = (message: ChatMessageTypeRequest) => {
    sendMessage({
      ...message,
      replyOnId: chat.messageId,
      replyOnMessage: chat.message,
    });

    handleSetActiveReply(null);
  };
  const handleCloseReply = () => {
    handleSetActiveReply(null);
  };

  return (
    <>
      <div className="reaction-button chat-reaction">
        <button className={likeState.isLiked ? 'active' : ''} onClick={() => likeChat(true)}>
          ❤️<span>{formatCompactNumber(likeState.likedCounter)}</span>
        </button>
        <button className={likeState.isDisliked ? 'active' : ''} onClick={() => likeChat(false)}>
          🖕<span>{formatCompactNumber(likeState.dislikedCounter)}</span>
        </button>
        <span className="reply-button cursor-pointer" onClick={handleReplyClick}>
          {t('comment.reply')}
        </span>
      </div>
      {activeReplyId === chat.messageId && (
        <ChatInput
          key={chat.messageId}
          sendMessage={onSendMessage}
          handleCloseReply={handleCloseReply}
          replyOnId={chat.messageId}
          handleBlur={() => {
            setTimeout(() => {
              handleSetActiveReply(null);
            }, 300);
          }}
        />
      )}
    </>
  );
};
